export type SectionType =
  // Front Matter
  | 'half-title'     // Just the title, no author
  | 'title-page'     // Full title page with author
  | 'copyright'      // Copyright notice
  | 'dedication'     // Dedication
  | 'epigraph'       // Quote/epigraph
  | 'toc'            // Table of contents
  | 'foreword'       // Written by someone else
  | 'preface'        // Written by author
  | 'introduction'   // Introduction to content
  // Body
  | 'part'           // Part divider
  | 'prologue'       // Before main story
  | 'chapter'        // Regular chapter
  | 'interlude'      // Between chapters
  | 'epilogue'       // After main story
  // Back Matter
  | 'afterword'      // Author's comments
  | 'acknowledgments'
  | 'appendix'       // Supplementary material
  | 'glossary'       // Definitions
  | 'bibliography'   // References
  | 'notes'          // End notes
  | 'about-author'   // Bio
  | 'also-by';       // Other books

export interface Scene {
  id: string;
  title: string;
  content: string;
  wordCount: number;
  order: number;
}

export interface ChapterDisplayOptions {
  showTitle: boolean;
  showNumber: boolean;
  numberingStyle: 'arabic' | 'roman' | 'word' | 'none';
  titlePrefix: string; // e.g., "Chapter", "Part", custom text
  startOnNewPage: boolean;
  dropCap: boolean;
}

export interface Chapter {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  order: number;
  wordCount: number;
  sectionType: SectionType;
  scenes?: Scene[];
  notes?: string;
  displayOptions?: ChapterDisplayOptions;
  image?: string; // For pages like About the Author
  createdAt: Date;
  updatedAt: Date;
}

// Section categories for clear organization
export const FRONT_MATTER_TYPES: SectionType[] = [
  'half-title', 'title-page', 'copyright', 'dedication',
  'epigraph', 'toc', 'foreword', 'preface', 'introduction'
];

export const BODY_TYPES: SectionType[] = [
  'part', 'prologue', 'chapter', 'interlude', 'epilogue'
];

export const BACK_MATTER_TYPES: SectionType[] = [
  'afterword', 'acknowledgments', 'appendix', 'glossary',
  'bibliography', 'notes', 'about-author', 'also-by'
];

// Only about-author gets an author photo upload
export const PAGES_WITH_IMAGE: SectionType[] = [
  'about-author'
];

export interface BookMetadata {
  title: string;
  subtitle?: string;
  author: string;
  description: string;
  genre: string;
  language: string;
  coverImage?: string;
  isbn?: string;
  publisher?: string;
  publishDate?: string;
  copyright?: string;
  keywords?: string[];
}

export interface WritingGoal {
  type: 'daily' | 'project' | 'session';
  targetWords: number;
  currentWords: number;
  deadline?: Date;
}

export interface WritingSession {
  id: string;
  startTime: Date;
  endTime?: Date;
  wordsWritten: number;
  chapterId?: string;
}

export interface Project {
  id: string;
  kind?: 'book' | 'manga';
  metadata: BookMetadata;
  chapters: Chapter[];
  writingGoal?: WritingGoal;
  sessions: WritingSession[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ExportOptions {
  format: 'pdf' | 'epub' | 'docx' | 'html';
  includeTitle: boolean;
  includeToc: boolean;
  includeCopyright: boolean;
  includeCover: boolean; // Include cover image in export
  printReady: boolean; // Export without cover for print services like KDP
  pageSize: 'a4' | 'a5' | 'letter' | '6x9' | '5x8' | '5.5x8.5';
  fontSize: 'small' | 'medium' | 'large';
  fontFamily: 'serif' | 'sans';
  lineSpacing: 'single' | '1.5' | 'double';
  margins: 'normal' | 'narrow' | 'wide';
  chapterStartPage: 'any' | 'right' | 'left';
  includePageNumbers: boolean;
  dropCaps: boolean; // Decorative large initial at the start of each chapter
  headerFooter: 'none' | 'title' | 'author' | 'chapter';
}

export interface EditorSettings {
  theme: 'light' | 'dark' | 'sepia' | 'system';
  autoSave: boolean;
  autoSaveInterval: number;
  fontFamily: 'serif' | 'sans' | 'mono';
  fontSize: number;
  lineHeight: 'compact' | 'normal' | 'relaxed';
  showWordCount: boolean;
  showCharCount: boolean;
  typewriterMode: boolean;
  focusModeOpacity: number;
  spellcheck: boolean;
  showChapterNumber: boolean;
  dropCaps: boolean;
  /** Writing canvas look: 'night' (dark, default) or 'paper' (warm vellum page). */
  canvasTheme: 'night' | 'paper';
  /** Live particle effect emitted at the caret while writing. */
  writingEffect: 'petals' | 'stardust' | 'inkrain' | 'sparks' | 'glitch' | 'mist' | 'fire' | 'fireflies' | 'neon' | 'glyphs' | 'none';
  /** Ghost-text continuation suggestions while writing (Tab to accept). */
  aiAutocomplete: boolean;
  /** How much text the AI continuation suggests: a sentence or a short paragraph. */
  aiAutocompleteLength: 'short' | 'long';
}

export type View =
  | 'dashboard'
  | 'projects'
  | 'editor'
  | 'export'
  | 'settings'
  | 'stats'
  | 'professionals'
  | 'messages'
  | 'pricing'
  | 'account';

export interface DailyStats {
  date: string;
  wordsWritten: number;
  timeSpent: number;
  sessionsCount: number;
}
